import { ChevronLeft, ChevronRight, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useWizard } from '../engine';

interface WizardNavigationProps {
  className?: string;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  floating?: boolean;
}

export function WizardNavigation({
  className,
  onSubmit,
  isSubmitting = false,
  floating = false,
}: WizardNavigationProps) {
  const {
    canGoNext,
    canGoBack,
    isFirstStep,
    isSummaryStep,
    goNext,
    goBack,
  } = useWizard();

  const handleNext = () => {
    const result = goNext();
    if (!result.success) {
      // Could show a toast here if needed
      console.warn('Navigation blocked:', result.error);
    }
  };

  const handleBack = () => {
    goBack();
  };

  const handleSubmit = () => {
    onSubmit?.();
  };

  // Floating variant - fixed to bottom right corner
  if (floating) {
    return (
      <div
        className={cn(
          'fixed right-6 z-50 flex items-center gap-3',
          // Same z-index as the support banner, which is fixed to bottom-0 and
          // sits later in the DOM: without this lift it paints over these
          // buttons and swallows their clicks. `--support-banner-h` is 0px
          // outside a support session, leaving the usual bottom-6.
          'bottom-[calc(1.5rem_+_var(--support-banner-h))]',
          className
        )}
      >
        {/* Back button */}
        {!isFirstStep && (
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={!canGoBack || isSubmitting}
            className="gap-2 bg-white shadow-lg hover:bg-gray-50"
            size="lg"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>
        )}

        {/* Next/Submit button */}
        {isSummaryStep ? (
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 bg-green-600 shadow-lg hover:bg-green-700"
            size="lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creando...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Crear Torneo
              </>
            )}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            disabled={!canGoNext || isSubmitting}
            className="gap-2 shadow-lg"
            size="lg"
          >
            Siguiente
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  // Default inline variant
  return (
    <div
      className={cn(
        'flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4',
        className
      )}
    >
      {/* Back button */}
      <div>
        {!isFirstStep && (
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={!canGoBack || isSubmitting}
            className="gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>
        )}
      </div>

      {/* Next/Submit button */}
      <div>
        {isSummaryStep ? (
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creando...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Crear Torneo
              </>
            )}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            disabled={!canGoNext || isSubmitting}
            className="gap-2"
          >
            Siguiente
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

